/**
 * Library grouping.
 *
 * R8's three buckets:
 *
 *   Watching     — aired episodes still unwatched
 *   Up to date   — caught up, but the show is still running
 *   Completed    — caught up, and the show has ended
 *
 * The distinction between the last two cannot be made from the library payload.
 * `not_aired_episodes_count` looks like the right field and is not: Simkl fills
 * it only once episodes are scheduled, so it reads 0 for 185 of 193 shows in a
 * real library and every show between seasons looks finished. The airing flag
 * comes from `/tv/{id}` instead — see store/airstatus.
 */
import type { LibraryShow } from "@/api/schemas";
import { remaining } from "@/lib/episode";

export type Bucket = "watching" | "uptodate" | "completed";

export const BUCKET_LABELS: Record<Bucket, string> = {
  watching: "Watching",
  uptodate: "Up to date",
  completed: "Completed",
};

/**
 * @param airing Whether the show is still running, from `/tv/{id}`. Undefined
 *   while that lookup is outstanding, in which case a scheduled episode is the
 *   only evidence available and absence of one is read as ended.
 */
export function bucketOf(show: LibraryShow, airing?: boolean): Bucket {
  if (remaining(show) > 0) return "watching";
  if (airing === undefined) return show.notAiredEpisodes > 0 ? "uptodate" : "completed";
  return airing ? "uptodate" : "completed";
}

export interface BucketGroup {
  bucket: Bucket;
  shows: LibraryShow[];
}

const ORDER: Bucket[] = ["watching", "uptodate", "completed"];

export function groupLibrary(
  shows: readonly LibraryShow[],
  airing?: ReadonlyMap<number, boolean>,
): BucketGroup[] {
  const groups = new Map<Bucket, LibraryShow[]>();
  for (const show of shows) {
    const bucket = bucketOf(show, airing?.get(show.show.ids.simkl));
    groups.set(bucket, [...(groups.get(bucket) ?? []), show]);
  }
  return ORDER.filter((bucket) => (groups.get(bucket)?.length ?? 0) > 0).map((bucket) => ({
    bucket,
    shows: (groups.get(bucket) ?? []).toSorted((a, b) => a.show.title.localeCompare(b.show.title)),
  }));
}

/** Fraction watched, for the progress bar under each poster. */
export function progress(show: LibraryShow): number {
  if (show.totalEpisodes === 0) return 0;
  return Math.min(1, show.watchedEpisodes / show.totalEpisodes);
}
